import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Flame, ZoomIn, ZoomOut } from "lucide-react";

const ITEM_BAR = {
  phase: "bg-gray-900 text-white",
  epic: "bg-purple-600 text-white",
  milestone: "bg-amber-500 text-white",
  story: "bg-emerald-600 text-white",
  bug: "bg-red-600 text-white",
  task: "bg-[#002FA7] text-white",
};

const Gantt = () => {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [dayWidth, setDayWidth] = useState(14); // px per day

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/projects/${projectId}/gantt`);
      setData(data);
    })();
  }, [projectId]);

  // Group tasks by parent_task_id so phases come above their children
  const ordered = useMemo(() => {
    if (!data) return [];
    const tasks = data.tasks;
    const byParent = {};
    for (const t of tasks) {
      const key = t.parent_task_id || "__root__";
      (byParent[key] = byParent[key] || []).push(t);
    }
    const out = [];
    const visit = (parentKey, depth) => {
      const children = byParent[parentKey] || [];
      // Roots/phases first by start
      children.sort((a, b) => (a.day_offset ?? 9e9) - (b.day_offset ?? 9e9));
      for (const t of children) {
        out.push({ ...t, depth });
        visit(t.task_id, depth + 1);
      }
    };
    visit("__root__", 0);
    return out;
  }, [data]);

  if (!data) return <div className="p-12 text-sm text-gray-500">Loading Gantt…</div>;

  const total = data.total_days || 1;
  const start = data.project_start ? new Date(data.project_start) : new Date();

  // Build month header
  const months = [];
  if (data.project_start && data.project_end) {
    const cursor = new Date(start);
    let lastMonthLabel = "";
    let monthStartDay = 0;
    for (let d = 0; d < total; d++) {
      const label = cursor.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
      if (label !== lastMonthLabel) {
        if (lastMonthLabel) {
          months.push({ label: lastMonthLabel, fromDay: monthStartDay, span: d - monthStartDay });
        }
        lastMonthLabel = label;
        monthStartDay = d;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    months.push({ label: lastMonthLabel, fromDay: monthStartDay, span: total - monthStartDay });
  }

  const labelWidth = 280;
  const timelineWidth = total * dayWidth;

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={`Gantt · ${data.project.name}`}
        title="Gantt & Critical Path"
        description={`Timeline visualisation with CPM. Critical path: ${data.critical_path.length} task${data.critical_path.length === 1 ? "" : "s"} · ${data.critical_path_length_days} days.`}
        actions={
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="sm" onClick={() => setDayWidth((w) => Math.max(6, w - 4))} className="rounded-none border-gray-900" data-testid="gantt-zoom-out">
              <ZoomOut className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDayWidth((w) => Math.min(40, w + 4))} className="rounded-none border-gray-900" data-testid="gantt-zoom-in">
              <ZoomIn className="w-3.5 h-3.5" />
            </Button>
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-project-from-gantt">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
          </div>
        }
      />

      <div className="md:hidden px-6 py-12 text-center text-sm text-gray-400">
        Gantt chart requires a wider screen. Please use a tablet or desktop.
      </div>
      <div className="hidden md:block px-5 sm:px-8 lg:px-12 py-6">
        {ordered.length === 0 || !data.project_start ? (
          <div className="border border-dashed border-[#374151] p-16 text-center text-sm text-gray-500">
            Add tasks with start &amp; due dates to see them on the Gantt.
          </div>
        ) : (
          <div className="border border-[#1F2937] bg-[#0D1117] overflow-auto" data-testid="gantt-canvas">
            <div className="flex" style={{ minWidth: labelWidth + timelineWidth }}>
              {/* Left: task labels */}
              <div className="flex-shrink-0 border-r border-[#1F2937]" style={{ width: labelWidth }}>
                <div className="h-14 border-b border-[#1F2937] bg-[#030712] px-4 flex items-end pb-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                  Work item
                </div>
                {ordered.map((t) => (
                  <div key={t.task_id} className="h-9 px-4 flex items-center justify-between border-b border-[#1F2937] text-sm gap-2" data-testid={`gantt-label-${t.task_id}`}>
                    <div style={{ paddingLeft: t.depth * 14 }} className="flex items-center gap-1.5 truncate min-w-0">
                      {t.is_critical && <Flame className="w-3 h-3 text-red-600 flex-shrink-0" data-testid={`critical-flag-${t.task_id}`} />}
                      <span className={`text-[9px] uppercase tracking-[0.18em] px-1 py-0.5 flex-shrink-0 ${t.item_type === "phase" ? "bg-gray-900 text-white" : "bg-[#111827] text-slate-300"}`}>{t.item_type}</span>
                      <span className={`truncate ${t.item_type === "phase" ? "font-semibold" : ""}`}>{t.title}</span>
                    </div>
                    {t.slack_days != null && t.slack_days > 0 && (
                      <span
                        className="text-[10px] font-mono tracking-tight text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 flex-shrink-0"
                        title={`Schedule float / slack: ${t.slack_days} day${t.slack_days === 1 ? "" : "s"} — can slip without delaying the project.`}
                        data-testid={`slack-badge-${t.task_id}`}
                      >
                        +{t.slack_days}d
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Right: timeline */}
              <div className="relative" style={{ width: timelineWidth }}>
                {/* Month header */}
                <div className="h-7 border-b border-[#1F2937] bg-[#030712] relative">
                  {months.map((m, i) => (
                    <div
                      key={`m-${i}`}
                      className="absolute top-0 h-full border-r border-[#1F2937] px-2 text-[11px] uppercase tracking-[0.2em] text-slate-400 flex items-center"
                      style={{ left: m.fromDay * dayWidth, width: m.span * dayWidth }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>
                {/* Day gridlines header (compact) */}
                <div className="h-7 border-b border-gray-200 bg-gray-100 relative">
                  {Array.from({ length: total }).map((_, d) => {
                    const date = new Date(start);
                    date.setDate(date.getDate() + d);
                    const isWeekStart = date.getDay() === 1;
                    return (
                      <div
                        key={`d-${d}`}
                        className={`absolute top-0 h-full text-[9px] text-gray-600 text-center ${isWeekStart ? "border-l border-gray-300 font-mono" : ""}`}
                        style={{ left: d * dayWidth, width: dayWidth }}
                      >
                        {dayWidth >= 18 ? date.getDate() : (isWeekStart ? date.getDate() : "")}
                      </div>
                    );
                  })}
                </div>

                {/* Task bars */}
                <div className="relative">
                  {/* Weekend bands */}
                  {Array.from({ length: total }).map((_, d) => {
                    const date = new Date(start);
                    date.setDate(date.getDate() + d);
                    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                    if (!isWeekend) return null;
                    return (
                      <div key={`w-${d}`} className="absolute top-0 bottom-0 bg-[#111827]" style={{ left: d * dayWidth, width: dayWidth }} />
                    );
                  })}

                  {ordered.map((t) => {
                    const offset = t.day_offset ?? 0;
                    const dur = t.duration_days ?? 1;
                    const left = offset * dayWidth;
                    const width = Math.max(dayWidth, dur * dayWidth - 2);
                    const isMilestone = t.item_type === "milestone";
                    const hasDates = t.day_offset != null && t.duration_days != null;
                    const barClass = ITEM_BAR[t.item_type] || ITEM_BAR.task;
                    return (
                      <div key={t.task_id} className="h-9 border-b border-[#1F2937] relative" data-testid={`gantt-row-${t.task_id}`}>
                        {hasDates && t.slack_days != null && t.slack_days > 0 && !isMilestone && (
                          <div
                            className="absolute top-1.5 h-6 border border-dashed border-emerald-400 bg-emerald-50/60"
                            style={{ left: left + width, width: t.slack_days * dayWidth }}
                            title={`Slack ${t.slack_days} day${t.slack_days === 1 ? "" : "s"} — extra room before this task delays the project`}
                            data-testid={`slack-ghost-${t.task_id}`}
                          />
                        )}
                        {hasDates && (
                          isMilestone ? (
                            <div
                              className="absolute top-1.5 w-6 h-6 bg-amber-500 rotate-45 border border-white shadow-sm"
                              style={{ left: left - 12 }}
                              title={t.title}
                              data-testid={`gantt-bar-${t.task_id}`}
                            />
                          ) : (
                            <div
                              className={`absolute top-1.5 h-6 px-2 flex items-center text-[11px] truncate ${t.is_critical ? "bg-red-500 text-gray-900 outline outline-2 outline-red-700 font-semibold" : barClass}`}
                              style={{ left, width }}
                              title={t.title}
                              data-testid={`gantt-bar-${t.task_id}`}
                            >
                              {dayWidth * dur > 60 && <span className="truncate">{t.title}</span>}
                            </div>
                          )
                        )}
                        {/* Today line */}
                      </div>
                    );
                  })}

                  {/* Today vertical line */}
                  {(() => {
                    const todayOffset = Math.floor((Date.now() - start.getTime()) / 86400000);
                    if (todayOffset < 0 || todayOffset > total) return null;
                    return (
                      <div
                        className="absolute top-0 bottom-0 border-l-2 border-[#002FA7] z-10 pointer-events-none"
                        style={{ left: todayOffset * dayWidth }}
                        title="Today"
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 bg-gray-900" /> Phase</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 bg-[#002FA7]" /> Task</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 bg-emerald-600" /> Story</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-4 bg-red-600" /> Bug</div>
          <div className="flex items-center gap-2"><span className="inline-block w-3 h-3 bg-amber-500 rotate-45" /> Milestone</div>
          <div className="flex items-center gap-2"><Flame className="w-3.5 h-3.5 text-red-600" /> On critical path</div>
          <div className="flex items-center gap-2"><span className="inline-block w-4 h-3 border border-dashed border-emerald-400 bg-emerald-50/60" /> Slack / float</div>
          <div className="flex items-center gap-2"><span className="inline-block w-0.5 h-4 bg-[#002FA7]" /> Today</div>
        </div>
      </div>{/* end hidden md:block */}
    </div>
  );
};

export default Gantt;
